import PropTypes from 'prop-types'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { eachDayOfInterval, format, isSameDay } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import useSnackbar from 'hooks/useSnackbar'
import * as Endpoints from 'services/api/AnalyticsReportsApi'
import { getPastDateInTimestamp } from 'utils/date'
import { isBeforeMetaBillingCutoff } from 'utils/metaBillingPeriod'
import { accumulatePricingPoint, adaptCategoryCardsForLegacy, createPricingDay } from 'utils/pricingAnalytics'
import { dateFns } from 'constants/index'
import usePricingAnalyticReport from 'mutations/usePricingAnalyticReport'
import useExportPricingAnalyticReport from 'mutations/useExportPricingAnalyticReport'

export const AnalyticsReportsContext = createContext()

function AnalyticsReportsProvider({ children }) {
  const [drawerIsOpen, setDrawerIsOpen] = useState(true)
  const [isSendFilter, setIsSendFilter] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [reportError, setReportError] = useState('')
  const [isAfterJune, setIsAfterJune] = useState(false)
  const [selectedTab, setSelectedTab] = useState('pricing')

  const { createSnackbar } = useSnackbar()

  // data and the period it was requested for change together, so labels, days and the billing mode
  // never describe a different period than the data on screen
  const [report, setReport] = useState(() => ({
    data: null,
    params: {
      wabaId: '',
      startDate: getPastDateInTimestamp(7),
      endDate: getPastDateInTimestamp(1, true),
      phoneNumbers: [],
    },
  }))
  const { data, params: filter } = report

  const [wabas, setWabas] = useState([])

  const { mutateAsync: getPricingMutateAsync } = usePricingAnalyticReport()
  const { mutateAsync: exportMutateAsync } = useExportPricingAnalyticReport()

  const toggleDrawer = useCallback(() => {
    setDrawerIsOpen((oldState) => !oldState)
  }, [])

  const toggleTab = useCallback(() => {
    setSelectedTab((oldState) => (oldState === 'pricing' ? 'conversation' : 'pricing'))
  }, [])

  const setOpenSnackbar = (msg) => {
    setIsOpen(true)
    setMessage(msg)
  }

  const setCloseSnackbar = () => {
    setIsOpen(false)
    setMessage('')
  }

  const calculateIsAfterJune = useCallback(() => {
    const startOfJune = new Date(2023, 5, 1)
    return filter.startDate * 1000 >= startOfJune.getTime() || filter.endDate * 1000 >= startOfJune.getTime()
  }, [filter.startDate, filter.endDate])

  useEffect(() => {
    const updatedIsAfterJune = calculateIsAfterJune()
    if (isAfterJune !== updatedIsAfterJune) {
      setIsAfterJune(updatedIsAfterJune)
    }
  }, [calculateIsAfterJune, isAfterJune])

  const [labels, timestamps] = useMemo(() => {
    const xLabels = eachDayOfInterval({
      start: filter.startDate * 1000,
      end: filter.endDate * 1000,
    }).map((date) => {
      const label = format(date, 'MMM dd', { locale: ptBR })
      return label[0].toUpperCase() + label.slice(1)
    })
    const timestampData = eachDayOfInterval({
      start: filter.startDate * 1000,
      end: filter.endDate * 1000,
    }).map((date) => {
      return {
        [format(date, 't', { locale: ptBR })]: {
          freeChat: {
            businessInitiated: { items: [], total: 0 },
            entryPoint: { items: [], total: 0 },
            freeLevel: { items: [], total: 0 },
            userInitiated: { items: [], total: 0 },
            utility: { items: [], total: 0 },
            amount: 0,
          },
          regularChat: {
            businessInitiated: { items: [], total: 0 },
            userInitiated: { items: [], total: 0 },
            amount: 0,
          },
          regularChatCategory: {
            marketing: { items: [], total: 0 },
            marketingLite: { items: [], total: 0 },
            utility: { items: [], total: 0 },
            authentication: { items: [], total: 0 },
            service: { items: [], total: 0 },
            amount: 0,
          },
          allChatCategory: {
            marketing: { items: [], total: 0 },
            marketingLite: { items: [], total: 0 },
            utility: { items: [], total: 0 },
            authentication: { items: [], total: 0 },
            service: { items: [], total: 0 },
            amount: 0,
          },
        },
      }
    })
    return [xLabels, timestampData]
  }, [filter])

  const isLegacyBilling = selectedTab === 'pricing' && isBeforeMetaBillingCutoff(filter.endDate)

  const isSameDate = useMemo(() => {
    return !!isSameDay(new Date(filter.startDate * 1000), new Date(filter.endDate * 1000))
  }, [filter.startDate, filter.endDate])

  const getReportData = useCallback(
    async ({ tab, ...filterParams }) => {
      let hasError = false
      setIsSendFilter(true)
      setReportError('')
      try {
        const response =
          tab && tab === 'conversation'
            ? await Endpoints.generateConversationsData(filterParams)
            : await getPricingMutateAsync(filterParams)
        setReport({ data: response.data, params: filterParams })
      } catch (requestError) {
        console.error(requestError)
        hasError = true
        setReportError(requestError?.message || 'Ocorreu um erro inesperado ao consultar o relatório.')
      } finally {
        setIsSendFilter(false)
      }
      return !hasError
    },
    [getPricingMutateAsync],
  )

  const getInitialData = useCallback(async () => {
    setIsSendFilter(true)
    try {
      const response = await Endpoints.getWabaList()
      if (response.data.length > 0) {
        setWabas(
          response.data.map((waba) => {
            const newWaba = waba
            delete newWaba.catalog
            return newWaba
          }),
        )

        const params = {
          wabaId: response.data[0].id,
          phoneNumbers: response.data[0].phoneNumbers,
          startDate: getPastDateInTimestamp(7),
          endDate: getPastDateInTimestamp(1, true),
        }

        await getReportData(params)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsSendFilter(false)
    }
  }, [getReportData])

  const defaultParams = useMemo(() => {
    let defaultValue = {
      wabaId: '',
      startDate: getPastDateInTimestamp(7),
      endDate: getPastDateInTimestamp(1, true),
      phoneNumbers: [],
    }

    if (wabas.length > 0) {
      defaultValue = {
        ...defaultValue,
        wabaId: wabas[0].id,
        phoneNumbers: wabas[0].phoneNumbers,
      }
    }
    return defaultValue
  }, [wabas])

  useEffect(() => {
    getInitialData()
  }, [getInitialData])

  const exportData = useCallback(
    async (tierType = 'ALL') => {
      try {
        const params = {
          wabaId: filter.wabaId !== '' ? filter.wabaId : wabas[0]?.id,
          startDate: filter.startDate,
          endDate: filter.endDate,
          phoneNumbers: filter.phoneNumbers.length > 0 ? filter.phoneNumbers : wabas[0].phoneNumbers,
          tier: tierType,
        }

        if (selectedTab === 'pricing') {
          const tierTypePricing = {
            ALL: 'ALL',
            FREE_TIER: 'FREE_CUSTOMER_SERVICE',
            REGULAR: 'REGULAR',
          }
          await exportMutateAsync({ ...params, tier: tierTypePricing[tierType] })
          createSnackbar(`Relatório exportado com sucesso`)
        } else {
          const response = await Endpoints.getConversationsGuid(params)
          if (response.data) {
            Endpoints.exportConversationsCsv(response.data)
            createSnackbar(`Relatório exportado com sucesso`)
          } else {
            throw new Error(`Erro na requisição. Status: ${response.status}`)
          }
        }
      } catch (error) {
        console.error(error)
        createSnackbar('Limite excedido. Para extrair grandes volumes de informação, utilize nossa API.')
      }
    },
    [
      filter.wabaId,
      filter.startDate,
      filter.endDate,
      filter.phoneNumbers,
      wabas,
      selectedTab,
      exportMutateAsync,
      createSnackbar,
    ],
  )

  const { cards, charts, cardsCategory } = useMemo(() => {
    let chartData = []
    const apiCards = {
      totalCategory: data?.totalCategory,
      freeTierCategory: data?.freeTierCategory,
      regularTierCategory: data?.regularTierCategory,
    }
    const pricingCards = isLegacyBilling ? adaptCategoryCardsForLegacy(apiCards) : apiCards

    if (selectedTab === 'pricing') {
      if (data?.dataPoints?.length) {
        chartData = timestamps.map((timestampItem) => {
          const [timeKey, value] = Object.entries(timestampItem)[0]

          const dataFiltered = data.dataPoints.filter((item) =>
            dateFns.isSameDay(item.start * 1000, Number(timeKey) * 1000),
          )

          const day = createPricingDay()
          dataFiltered.forEach((point) => accumulatePricingPoint(day, point, { legacy: isLegacyBilling }))

          const labelKey = format(new Date(Number(timeKey) * 1000), 'MMM dd', { locale: ptBR })
          return {
            [labelKey[0].toUpperCase() + labelKey.slice(1)]: {
              ...value,
              ...day,
              date: format(new Date(Number(timeKey) * 1000), 'dd/MM/yy', { locale: ptBR }),
            },
          }
        })
      }
    } else if (data?.data?.length) {
      chartData = timestamps.map((timestampItem) => {
        const [timeKey, value] = Object.entries(timestampItem)[0]

        const dataFiltered = data.data.filter((item) => dateFns.isSameDay(item.start * 1000, Number(timeKey) * 1000))

        if (dataFiltered.length > 0) {
          dataFiltered.reduce((accumulator, currentValue) => {
            if (isSameDate) {
              if (currentValue.conversation_category === 'MARKETING') {
                accumulator.allChatCategory.marketing.items.push(currentValue)
                accumulator.allChatCategory.marketing.total += currentValue.conversation
              }

              if (currentValue.conversation_category === 'UTILITY') {
                accumulator.allChatCategory.utility.items.push(currentValue)
                accumulator.allChatCategory.utility.total += currentValue.conversation
              }

              if (currentValue.conversation_category === 'AUTHENTICATION') {
                accumulator.allChatCategory.authentication.items.push(currentValue)
                accumulator.allChatCategory.authentication.total += currentValue.conversation
              }

              if (currentValue.conversation_category === 'SERVICE') {
                accumulator.allChatCategory.service.items.push(currentValue)
                accumulator.allChatCategory.service.total += currentValue.conversation
              }

              if (currentValue?.conversation_type === 'FREE_TIER') {
                if (currentValue.conversation_direction === 'BUSINESS_INITIATED') {
                  accumulator.freeChat.businessInitiated.items.push(currentValue)
                  accumulator.freeChat.businessInitiated.total += currentValue.conversation
                }
                if (currentValue.conversation_direction === 'USER_INITIATED') {
                  accumulator.freeChat.userInitiated.items.push(currentValue)
                  accumulator.freeChat.userInitiated.total += currentValue.conversation
                }
              }

              if (currentValue?.conversation_type === 'REGULAR') {
                if (currentValue.conversation_direction === 'BUSINESS_INITIATED') {
                  accumulator.regularChat.businessInitiated.items.push(currentValue)
                  accumulator.regularChat.businessInitiated.total += currentValue.conversation
                }
                if (currentValue.conversation_direction === 'USER_INITIATED') {
                  accumulator.regularChat.userInitiated.items.push(currentValue)
                  accumulator.regularChat.userInitiated.total += currentValue.conversation
                }
              }

              accumulator.allChatCategory.amount =
                accumulator.allChatCategory.marketing.total +
                accumulator.allChatCategory.utility.total +
                accumulator.allChatCategory.authentication.total +
                accumulator.allChatCategory.service.total
            } else {
              if (currentValue?.conversation_type === 'FREE_TIER') {
                if (currentValue.conversation_direction === 'BUSINESS_INITIATED') {
                  accumulator.freeChat.businessInitiated.items.push(currentValue)
                  accumulator.freeChat.businessInitiated.total += currentValue.conversation
                }
                if (currentValue.conversation_direction === 'USER_INITIATED') {
                  accumulator.freeChat.userInitiated.items.push(currentValue)
                  accumulator.freeChat.userInitiated.total += currentValue.conversation
                }
                if (currentValue.conversation_category === 'SERVICE') {
                  accumulator.freeChat.freeLevel.items.push(currentValue)
                  accumulator.freeChat.freeLevel.total += currentValue.conversation
                }
              }

              if (currentValue?.conversation_type === 'FREE_ENTRY_POINT') {
                if (currentValue.conversation_category === 'SERVICE') {
                  accumulator.freeChat.entryPoint.items.push(currentValue)
                  accumulator.freeChat.entryPoint.total += currentValue.conversation
                }
              }

              if (currentValue?.conversation_type === 'REGULAR') {
                if (currentValue.conversation_direction === 'BUSINESS_INITIATED') {
                  accumulator.regularChat.businessInitiated.items.push(currentValue)
                  accumulator.regularChat.businessInitiated.total += currentValue.conversation
                }
                if (currentValue.conversation_direction === 'USER_INITIATED') {
                  accumulator.regularChat.userInitiated.items.push(currentValue)
                  accumulator.regularChat.userInitiated.total += currentValue.conversation
                }

                if (currentValue.conversation_category === 'SERVICE') {
                  accumulator.regularChatCategory.service.items.push(currentValue)
                  accumulator.regularChatCategory.service.total += currentValue.conversation
                }

                if (currentValue.conversation_category === 'MARKETING') {
                  accumulator.regularChatCategory.marketing.items.push(currentValue)
                  accumulator.regularChatCategory.marketing.total += currentValue.conversation
                }

                if (currentValue.conversation_category === 'MARKETING_LITE') {
                  accumulator.regularChatCategory.marketingLite.items.push(currentValue)
                  accumulator.regularChatCategory.marketingLite.total += currentValue.conversation
                }

                if (currentValue.conversation_category === 'UTILITY') {
                  accumulator.regularChatCategory.utility.items.push(currentValue)
                  accumulator.regularChatCategory.utility.total += currentValue.conversation
                }

                if (currentValue.conversation_category === 'AUTHENTICATION') {
                  accumulator.regularChatCategory.authentication.items.push(currentValue)
                  accumulator.regularChatCategory.authentication.total += currentValue.conversation
                }
              }

              accumulator.freeChat.amount =
                accumulator.freeChat.businessInitiated.total +
                accumulator.freeChat.userInitiated.total +
                accumulator.freeChat.freeLevel.total +
                accumulator.freeChat.entryPoint.total

              accumulator.regularChatCategory.amount =
                accumulator.regularChatCategory.service.total +
                accumulator.regularChatCategory.marketing.total +
                accumulator.regularChatCategory.marketingLite.total +
                accumulator.regularChatCategory.utility.total +
                accumulator.regularChatCategory.authentication.total

              accumulator.regularChat.amount =
                accumulator.regularChat.businessInitiated.total + accumulator.regularChat.userInitiated.total
            }

            return accumulator
          }, value)
        }

        const labelKey = format(new Date(Number(timeKey) * 1000), 'MMM dd', { locale: ptBR })
        return {
          [labelKey[0].toUpperCase() + labelKey.slice(1)]: {
            ...value,
            date: format(new Date(Number(timeKey) * 1000), 'dd/MM/yy', { locale: ptBR }),
          },
        }
      })
    }

    return {
      cards: {
        allChat: data?.total,
        freeChat: data?.freeTier,
        regularChat: data?.regularTier,
      },
      charts: {
        labels,
        data: chartData,
      },
      cardsCategory: {
        allChatCategory: pricingCards.totalCategory,
        freeChat: selectedTab === 'pricing' ? pricingCards.freeTierCategory : data?.freeTier,
        regularChatCategory: pricingCards.regularTierCategory,
      },
    }
  }, [
    data?.data,
    data?.dataPoints,
    data?.freeTier,
    data?.freeTierCategory,
    data?.regularTier,
    data?.regularTierCategory,
    data?.totalCategory,
    data?.total,
    labels,
    timestamps,
    selectedTab,
    isSameDate,
    isLegacyBilling,
  ])

  const contextValue = useMemo(
    () => ({
      state: {
        drawerIsOpen,
        filter,
        cards,
        charts,
        cardsCategory,
        wabas,
        isSendFilter,
        defaultParams,
        message,
        isOpen,
        isAfterJune,
        isLegacyBilling,
        selectedTab,
        error: reportError,
      },
      actions: { toggleDrawer, getReportData, exportData, setOpenSnackbar, setCloseSnackbar, toggleTab },
    }),
    [
      cards,
      cardsCategory,
      charts,
      defaultParams,
      drawerIsOpen,
      exportData,
      filter,
      getReportData,
      isSendFilter,
      toggleDrawer,
      wabas,
      isOpen,
      message,
      isAfterJune,
      isLegacyBilling,
      toggleTab,
      selectedTab,
      reportError,
    ],
  )

  return <AnalyticsReportsContext.Provider value={contextValue}>{children}</AnalyticsReportsContext.Provider>
}

AnalyticsReportsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default AnalyticsReportsProvider
